import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Field } from "@/components/common";
import { HelpTip } from "@/components/HelpTip";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NumInput } from "@/components/NumInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, brlUnit, errMsg, UNITS, UNIT_LABELS } from "@/lib/api";

const EMPTY = { name: "", unit: "g", package_qty: "", package_price: "", supplier: "", stock_qty: "", stock_min: "" };

export const IngredientDialog = ({ open, onOpenChange, ingredient, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(ingredient ? { ...ingredient } : EMPTY); }, [ingredient, open]);
  const perUnit = (Number(f.package_price) || 0) / (Number(f.package_qty) || 1);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: f.name, unit: f.unit, package_qty: Number(f.package_qty) || 1, package_price: Number(f.package_price) || 0, supplier: f.supplier || "", stock_qty: Number(ingredient?.stock_qty) || 0, stock_min: Number(ingredient?.stock_min) || 0 };
      const r = ingredient?.id ? await api.put(`/ingredients/${ingredient.id}`, body) : await api.post("/ingredients", body);
      toast.success(ingredient?.id ? "Insumo atualizado" : "Insumo cadastrado");
      window.dispatchEvent(new Event("pb:stock-changed"));
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ingredient-dialog">
        <DialogHeader><DialogTitle className="font-display">{ingredient?.id ? "Editar insumo" : "Novo insumo"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome"><Input data-testid="ingredient-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Maionese artesanal" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Qtd. por embalagem" help={<HelpTip title="Quantidade por embalagem" testId="help-package-qty">Informe a quantidade total contida na embalagem de compra, utilizando a mesma unidade de medida que você costuma utilizar na ficha técnica (ex: se você compra um pacote de 2 kg mas costuma fracionar e usar em gramas, informe 2000 g).</HelpTip>}>
              <NumInput data-testid="ingredient-package-qty" step="any" min="0.0001" required value={f.package_qty} onChange={(e) => setF({ ...f, package_qty: e.target.value })} placeholder="700" className="num" />
            </Field>
            <Field label="Unidade de medida">
              <Select value={f.unit} onValueChange={(v) => setF({ ...f, unit: v })}>
                <SelectTrigger data-testid="ingredient-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u} data-testid={`ingredient-unit-${u}`}>{UNIT_LABELS[u]}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Preço da embalagem (R$)"><NumInput data-testid="ingredient-package-price" step="0.01" min="0" required value={f.package_price} onChange={(e) => setF({ ...f, package_price: e.target.value })} placeholder="10,00" className="num" /></Field>
          <div className="rounded-lg bg-secondary/50 px-3 py-2 text-sm flex justify-between" data-testid="ingredient-unit-price">
            <span className="text-muted-foreground">Preço por {f.unit}</span><span className="num font-semibold">{brlUnit(perUnit)} / {f.unit}</span>
          </div>
          <Field label="Fornecedor (opcional)"><Input data-testid="ingredient-supplier" value={f.supplier || ""} onChange={(e) => setF({ ...f, supplier: e.target.value })} /></Field>
          <p className="text-xs text-muted-foreground">Estoque atual e mínimo são gerenciados na aba <span className="font-medium text-foreground">Compras &amp; Estoque</span>.</p>
          <Button data-testid="ingredient-save" type="submit" className="w-full" disabled={saving}>{saving ? "Salvando..." : "Salvar insumo"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
